"use client";

import type { ReactNode } from "react";
import {
  HIGHLIGHT_TONES,
  type HighlightTone,
} from "@/components/tool-kit/highlighted-text";
import { TextAreaField } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";

/**
 * The input block and colour key every `style-checkers-a` widget opens with: a text area,
 * a "Try an example" button and a "Clear" button. All text arrives as props from the
 * widget's own strings object.
 */
export function CheckerInput({
  id,
  label,
  hint,
  placeholder,
  value,
  onChange,
  exampleLabel,
  clearLabel,
  example,
  extra,
}: {
  id: string;
  label: string;
  hint: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  exampleLabel: string;
  clearLabel: string;
  example: string;
  /** Options shown beside the buttons (a style picker, a switch). */
  extra?: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <TextAreaField
        hint={hint}
        id={id}
        label={label}
        onChange={onChange}
        placeholder={placeholder}
        prose
        rows={10}
        value={value}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => onChange(example)}
          type="button"
        >
          {exampleLabel}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={value.length === 0}
          onClick={() => onChange("")}
          type="button"
        >
          {clearLabel}
        </button>
        {extra}
      </div>
    </div>
  );
}

/** A row of coloured chips that names what each highlight colour means. */
export function ToneLegend({
  items,
}: {
  items: readonly { tone: HighlightTone; label: string }[];
}) {
  return (
    <p className="flex flex-wrap gap-3 text-sm">
      {items.map((item) => (
        <span className={HIGHLIGHT_TONES[item.tone]} key={item.label}>
          {item.label}
        </span>
      ))}
    </p>
  );
}

/** A sentence with one stretch marked, for the cards under a checker's highlighted text. */
export function MarkedSentence({
  sentence,
  from,
  to,
  tone,
}: {
  sentence: string;
  /** Offsets into `sentence`. */
  from: number;
  to: number;
  tone: HighlightTone;
}) {
  return (
    <p className="text-foreground text-sm leading-relaxed">
      {sentence.slice(0, from)}
      <mark className={HIGHLIGHT_TONES[tone]}>{sentence.slice(from, to)}</mark>
      {sentence.slice(to)}
    </p>
  );
}
